import AIChatWidget from '@/components/aichat/AiChatWidget';
import ExternalEmbed from '@/components/landing/external-embed';
import SdgIconStrip from '@/components/landing/sdg-icon-strip';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Deferred, Link } from '@inertiajs/react';
import {
    ArrowRight,
    Award,
    BookOpen,
    Building2,
    Calendar,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Clock,
    ExternalLink,
    GraduationCap,
    MapPin,
    Send,
    Sparkles,
    Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');
const defaultHeroSlides = [
    {
        image: '/images/hero/1.webp',
        imageAlt: 'Northeastern College hero banner slide one',
        title: 'Northeastern College',
        subtitle: 'Excellence in Education',
        description: 'Empowering learners through quality programs, strong values, and meaningful campus experiences.',
        buttonText: 'Learn More',
        buttonLink: '/about/history',
    },
    {
        image: '/images/hero/nc1.png',
        imageAlt: 'Northeastern College hero banner slide two',
        title: 'Future-Ready Programs',
        subtitle: 'Academic and Professional Growth',
        description: 'Discover courses and opportunities designed to prepare students for real-world impact.',
        buttonText: 'Explore Programs',
        buttonLink: '/research',
    },
    {
        image: '/images/hero/nc2.png',
        imageAlt: 'Northeastern College hero banner slide three',
        title: 'A Connected Campus Community',
        subtitle: 'People, Purpose, Progress',
        description: 'Stay updated with campus life, events, and initiatives that shape the Northeastern College experience.',
        buttonText: 'Contact Us',
        buttonLink: '/contact',
    },
];
const HERO_AUTOPLAY = true;
const heroQuickLinks = [
    {
        name: 'SIAS Online',
        description: 'Grades, enrollment, fees',
        url: 'https://icloudph.com/nc/sias/',
        external: true,
    },

    {
        name: 'Virtual Assistant',
        description: 'Ask Navia for help',
        url: 'https://virtual-assistant.northeasterncollege.edu.ph/kiosk',
        external: true,
    },
    {
        name: 'Course Offerings',
        description: 'Browse available programs',
        url: '/course-offerings',
    },
    {
        name: 'Calendar',
        description: 'Events & deadlines',
        url: '/collegiate-calendar',
    },
    {
        name: 'Message Us',
        description: 'We can help',
        url: 'https://www.facebook.com/NCnianAko',
        external: true,
    },
];

type NewsItem = {
    id: number;
    title: string;
    category: string;
    description: string;
    image: string;
    image_responsive?: ResponsiveImage | null;
    link?: string | null;
    created_at?: string;
    date?: string;
    sdgs?: Array<{
        sdg_number: number;
        short_title: string;
        slug: string;
        image?: string | null;
    }>;
};

type EventItem = {
    id: number;
    title: string;
    venue: string;
    start_datetime: string;
    end_datetime: string;
    status?: 'Upcoming' | 'On-going' | 'Ended' | string;
};

type CareerItem = {
    id: number;
    title: string;
    category: string;
    positions_available?: number | null;
    description?: string | null;
    qualifications?: string[] | null;
    created_at?: string | null;
    updated_at?: string | null;
    link?: string | null;
};

type HolidayItem = {
    name: string;
    date: string;
    type: 'Regular Holiday' | 'Special Non-Working Day' | 'Special Working Day';
};

type VideoItem = {
    id: number;
    title?: string;
    link: string;
    youtube?: { embed_url: string; watch_url: string } | null;
};

type Institute = {
    id: number;
    name: string;
    logo: string;
    logo_responsive?: ResponsiveImage | null;
    url?: string | null;
};

type CarouselItem = {
    id: number;
    image: string;
    image_responsive?: ResponsiveImage | null;
    status?: 'Active' | 'Inactive' | string;
    sort_order?: number;
};

type ResponsiveImage = {
    src: string;
    srcSet?: string | null;
    sizes?: string | null;
};

type HeroSlide = {
    image: string;
    imageAlt: string;
    title: string;
    subtitle: string;
    description: string;
    buttonText: string;
    buttonLink: string;
    imageResponsive?: ResponsiveImage | null;
};

type Research = {
    id: number;
    title: string;
    date: string;
    img: string;
    link: string;
    institute?: string | null;
    institute_id?: number | null;
};

const storageUrl = (value: string, directory: string) => {
    const placeholder = `${baseUrl}/images/logo.webp`;
    if (!value) return placeholder;
    if (value.startsWith('https://')) return value;
    if (value.startsWith('http://')) return `https://${value.slice(7)}`;
    if (value.startsWith('/storage/')) return `${baseUrl}${value}`;
    return `${baseUrl}/storage/${directory}/${value}`;
};

const imageAttrs = (responsive: ResponsiveImage | null | undefined, fallback: string) => ({
    src: responsive?.src ?? fallback,
    srcSet: responsive?.srcSet ?? undefined,
    sizes: responsive?.sizes ?? undefined,
});

interface LandingHomeProps {
    carousels?: CarouselItem[];
    news: NewsItem[];
    events: EventItem[];
    careers?: CareerItem[];
    videos?: VideoItem[];
    institutes: Institute[];
}

function formatNewsDate(item: NewsItem): string | null {
    const raw = item.date ?? item.created_at;
    if (!raw) return null;

    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;

    return d.toLocaleDateString('en-PH', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

function truncate(text: string, limit = 120): string {
    if (!text) return '';
    return text.length > limit ? text.slice(0, limit - 3) + '...' : text;
}

function formatEventShortDate(start_datetime: string): string {
    const d = new Date(start_datetime);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}

function parseEventDate(value: string): Date | null {
    if (!value) return null;
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(value: Date): Date {
    const next = new Date(value);
    next.setHours(0, 0, 0, 0);
    return next;
}

function datesInRange(start: Date, end: Date): Date[] {
    const rangeStart = startOfDay(start);
    const rangeEnd = startOfDay(end);

    if (rangeEnd.getTime() < rangeStart.getTime()) {
        return [rangeStart];
    }

    const days: Date[] = [];
    const cursor = new Date(rangeStart);

    while (cursor.getTime() <= rangeEnd.getTime()) {
        days.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }

    return days;
}

function formatEventShortDateRange(start_datetime: string, end_datetime: string): string {
    const start = parseEventDate(start_datetime);
    const end = parseEventDate(end_datetime);

    if (!start) return '';
    if (!end || startOfDay(start).getTime() === startOfDay(end).getTime()) {
        return formatEventShortDate(start_datetime);
    }

    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();

    if (sameMonth) {
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}-${end.toLocaleDateString('en-US', { day: 'numeric' })}`;
    }

    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}-${end.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: sameYear ? undefined : 'numeric',
    })}`;
}

function formatCareerCount(count?: number | null): string {
    const positions = count ?? 1;

    return positions === 1 ? '1 Open Role' : `${positions} Open Roles`;
}

function formatCareerDate(value?: string | null): string {
    if (!value) return 'Recently';

    const d = new Date(value);
    if (isNaN(d.getTime())) return 'Recently';

    return d.toLocaleDateString('en-PH', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function renderVideoEmbed(video: VideoItem) {
    const trimmed = (video.link ?? '').trim();
    if (!trimmed) return null;

    if (video.youtube) {
        return (
            <div className="video-embed">
                <iframe
                    src={video.youtube.embed_url.replace('www.youtube.com', 'www.youtube-nocookie.com')}
                    title={video.title || 'Northeastern College video'}
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="aspect-video w-full border-0"
                />
            </div>
        );
    }

    if (trimmed.includes('<iframe')) {
        return <div className="video-embed" dangerouslySetInnerHTML={{ __html: trimmed }} />;
    }

    return (
        <div className="video-embed">
            <iframe
                src={trimmed}
                title="Embedded video"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen={true}
                className="aspect-video w-full"
            />
        </div>
    );
}

function getLastMondayOfAugust(year: number): string {
    const date = new Date(year, 7, 31);

    while (date.getDay() !== 1) {
        date.setDate(date.getDate() - 1);
    }

    return `${year}-08-${String(date.getDate()).padStart(2, '0')}`;
}

function getPhilippineHolidays(year: number): HolidayItem[] {
    const holidays: HolidayItem[] = [
        { name: "New Year's Day", date: `${year}-01-01`, type: 'Regular Holiday' },
        { name: 'Araw ng Kagitingan', date: `${year}-04-09`, type: 'Regular Holiday' },
        { name: 'Labor Day', date: `${year}-05-01`, type: 'Regular Holiday' },
        { name: 'Independence Day', date: `${year}-06-12`, type: 'Regular Holiday' },
        { name: 'National Heroes Day', date: getLastMondayOfAugust(year), type: 'Regular Holiday' },
        { name: 'Ninoy Aquino Day', date: `${year}-08-21`, type: 'Special Non-Working Day' },
        { name: "All Saints' Day", date: `${year}-11-01`, type: 'Special Non-Working Day' },
        { name: 'Bonifacio Day', date: `${year}-11-30`, type: 'Regular Holiday' },
        {
            name: 'Feast of the Immaculate Conception of Mary',
            date: `${year}-12-08`,
            type: 'Special Non-Working Day',
        },
        { name: 'Christmas Eve', date: `${year}-12-24`, type: 'Special Non-Working Day' },
        { name: 'Christmas Day', date: `${year}-12-25`, type: 'Regular Holiday' },
        { name: 'Rizal Day', date: `${year}-12-30`, type: 'Regular Holiday' },
        { name: 'Last Day of the Year', date: `${year}-12-31`, type: 'Special Non-Working Day' },
    ];

    if (year === 2026) {
        holidays.push(
            { name: 'Chinese New Year', date: '2026-02-17', type: 'Special Non-Working Day' },
            { name: 'EDSA People Power Revolution Anniversary', date: '2026-02-25', type: 'Special Working Day' },
            { name: "Eid'l Fitr", date: '2026-03-20', type: 'Regular Holiday' },
            { name: 'Maundy Thursday', date: '2026-04-02', type: 'Regular Holiday' },
            { name: 'Good Friday', date: '2026-04-03', type: 'Regular Holiday' },
            { name: 'Black Saturday', date: '2026-04-04', type: 'Special Non-Working Day' },
            { name: "All Souls' Day", date: '2026-11-02', type: 'Special Non-Working Day' },
        );
    }

    return holidays.sort((a, b) => a.date.localeCompare(b.date));
}

function MonthlyEventsCalendar({ events }: { events: EventItem[] }) {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const holidays = getPhilippineHolidays(year);

    const firstDay = new Date(year, month, 1);
    const firstWeekday = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const eventDays = new Set<number>();
    events.forEach((event) => {
        const start = parseEventDate(event.start_datetime);
        const end = parseEventDate(event.end_datetime);

        if (!start) return;

        datesInRange(start, end ?? start).forEach((day) => {
            if (day.getFullYear() === year && day.getMonth() === month) {
                eventDays.add(day.getDate());
            }
        });
    });

    const holidayMap = new Map<number, HolidayItem[]>();
    holidays.forEach((holiday) => {
        const d = new Date(`${holiday.date}T00:00:00`);
        if (d.getFullYear() === year && d.getMonth() === month) {
            const current = holidayMap.get(d.getDate()) ?? [];
            current.push(holiday);
            holidayMap.set(d.getDate(), current);
        }
    });

    const days: number[] = [];
    for (let i = 1; i <= daysInMonth; i++) {
        days.push(i);
    }

    const weeks: (number | null)[][] = [];
    let currentWeek: (number | null)[] = [];

    for (let i = 0; i < firstWeekday; i++) {
        currentWeek.push(null);
    }

    days.forEach((day) => {
        if (currentWeek.length === 7) {
            weeks.push(currentWeek);
            currentWeek = [];
        }
        currentWeek.push(day);
    });

    if (currentWeek.length > 0) {
        while (currentWeek.length < 7) currentWeek.push(null);
        weeks.push(currentWeek);
    }

    const monthLabel = today.toLocaleDateString('en-PH', {
        month: 'long',
        year: 'numeric',
    });

    return (
        <div className="bg-card border-border mb-6 overflow-visible rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <div>
                    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">This Month</p>
                    <p className="text-foreground text-sm font-semibold md:text-base">{monthLabel}</p>
                </div>
            </div>

            <div className="px-4 pb-4">
                <div className="text-muted-foreground mb-2 grid grid-cols-7 text-center text-xs md:text-[13px]">
                    <span>Sun</span>
                    <span>Mon</span>
                    <span>Tue</span>
                    <span>Wed</span>
                    <span>Thu</span>
                    <span>Fri</span>
                    <span>Sat</span>
                </div>

                <div className="space-y-1">
                    {weeks.map((week, i) => (
                        <div key={i} className="grid grid-cols-7 gap-y-1 text-center text-xs md:text-[13px]">
                            {week.map((day, idx) => {
                                if (day === null) {
                                    return <div key={idx} />;
                                }

                                const isToday = day === today.getDate();
                                const hasEvent = eventDays.has(day);
                                const hasHoliday = holidayMap.has(day);
                                const dayHolidays = holidayMap.get(day) ?? [];
                                const holidayTooltip = dayHolidays.map((holiday) => `${holiday.name} (${holiday.type})`).join('\n');

                                let classes =
                                    'mx-auto flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full transition-colors text-[13px]';

                                if (hasEvent && hasHoliday) {
                                    classes += ' bg-primary text-primary-foreground ring-2 ring-amber-500 font-semibold';
                                } else if (hasHoliday && isToday) {
                                    classes += ' bg-amber-700 text-white ring-2 ring-primary font-semibold';
                                } else if (hasHoliday) {
                                    classes += ' bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold border border-amber-500/20';
                                } else if (hasEvent && isToday) {
                                    classes += ' bg-emerald-600 text-white font-semibold';
                                } else if (hasEvent) {
                                    classes += ' bg-emerald-500/10 text-emerald-600 dark:text-[#21d36b] font-semibold border border-emerald-500/20';
                                } else if (isToday) {
                                    classes += ' border border-emerald-500 text-emerald-600 dark:text-[#21d36b] font-semibold';
                                } else {
                                    classes += ' text-foreground/80';
                                }

                                return (
                                    <div key={idx} className="group relative flex justify-center">
                                        <div className={classes} tabIndex={holidayTooltip ? 0 : -1} aria-label={holidayTooltip || undefined}>
                                            {day}
                                        </div>
                                        {holidayTooltip && (
                                            <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-max max-w-[220px] -translate-x-1/2 rounded-md bg-[#111827] px-2 py-1.5 text-left text-[11px] leading-snug text-white shadow-lg group-focus-within:block group-hover:block">
                                                <div className="absolute top-full left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1 rotate-45 bg-[#111827]" />
                                                <div className="relative whitespace-pre-line">{holidayTooltip}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <div className="text-muted-foreground mt-3 flex items-center gap-2 text-xs md:text-[13px]">
                    <span className="inline-flex h-3 w-3 rounded-full border border-emerald-500 bg-emerald-500/20" />
                    <span>Dates with scheduled events</span>
                </div>

                <div className="text-muted-foreground mt-2 flex items-center gap-2 text-xs md:text-[13px]">
                    <span className="inline-flex h-3 w-3 rounded-full border border-amber-500 bg-amber-500/20" />
                    <span>Philippine holidays</span>
                </div>
            </div>
        </div>
    );
}

export default function LandingHome({ carousels = [], news, events, careers = [], videos = [], institutes }: LandingHomeProps) {
    const latestVideos = videos.slice(0, 2);
    const heroSlides: HeroSlide[] =
        carousels.length > 0
            ? carousels.map((carousel, index) => {
                  const fallbackSlide = defaultHeroSlides[index % defaultHeroSlides.length];
                  return {
                      ...fallbackSlide,
                      image: storageUrl(carousel.image, 'carousels/images'),
                      imageResponsive: carousel.image_responsive,
                      imageAlt: `Northeastern College hero carousel slide ${index + 1}`,
                  };
              })
            : defaultHeroSlides;
    const [isLoading, setIsLoading] = useState(true);
    const [activeCarouselSlide, setActiveCarouselSlide] = useState(0);

    useEffect(() => {
        const timeout = window.setTimeout(() => setIsLoading(false), 800);

        return () => {
            window.clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        if (!HERO_AUTOPLAY) return;

        const intervalId = window.setInterval(() => {
            setActiveCarouselSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6500);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [heroSlides.length]);

    useEffect(() => {
        if (activeCarouselSlide >= heroSlides.length) {
            setActiveCarouselSlide(0);
        }
    }, [activeCarouselSlide, heroSlides.length]);

    const handleNewsReadMoreClick = (newsId: number) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        if (!csrfToken) {
            return;
        }

        void fetch(route('news.track-read-more-click', { news: newsId }), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            keepalive: true,
        });
    };

    const handleCareerReadMoreClick = (careerId: number) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        if (!csrfToken) {
            return;
        }

        void fetch(route('careers.track-read-more-click', { career: careerId }), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            keepalive: true,
        });
    };

    return (
        <LandingLayout>
            {isLoading && (
                <div className="bg-background/90 fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-4">
                        <span className="border-muted-foreground/40 h-12 w-12 animate-spin rounded-full border-4 border-t-[#0A7A35]" />
                        <span className="text-muted-foreground text-sm font-medium">Loading…</span>
                    </div>
                </div>
            )}
            <SeoHead
                title="Home"
                description="Northeastern College in Santiago City, Isabela offers programs from basic education to postgraduate studies, with news, events, and admissions information."
                image={`${baseUrl}/images/logo.webp`}
            />

            {/* Hero Section */}
            <section className="relative bg-black py-0">
                <div className="grid lg:min-h-[590px] lg:grid-cols-[3.2fr_1fr]">
                    <div className="relative min-h-[260px] overflow-hidden sm:min-h-[380px] md:min-h-[460px] lg:min-h-[590px]">
                        {heroSlides.map((slide, index) => (
                            <img
                                key={`banner-${slide.title}-${index}`}
                                {...imageAttrs(slide.imageResponsive, slide.image)}
                                alt={slide.imageAlt}
                                className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-700 ${activeCarouselSlide === index ? 'scale-100 opacity-100' : 'pointer-events-none scale-[1.035] opacity-0'}`}
                                loading={index === 0 ? 'eager' : 'lazy'}
                                decoding="async"
                                fetchPriority={index === 0 ? 'high' : 'auto'}
                            />
                        ))}

                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-black/5 to-transparent" />

                        <button
                            type="button"
                            onClick={() => setActiveCarouselSlide((prev) => (prev === 0 ? heroSlides.length - 1 : prev - 1))}
                            aria-label="Previous banner"
                            className="absolute top-1/2 left-2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/35 text-white backdrop-blur transition hover:bg-black/55 sm:left-4 sm:h-11 sm:w-11"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveCarouselSlide((prev) => (prev + 1) % heroSlides.length)}
                            aria-label="Next banner"
                            className="absolute top-1/2 right-2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/35 text-white backdrop-blur transition hover:bg-black/55 sm:right-4 sm:h-11 sm:w-11"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>

                        <div className="absolute inset-x-0 bottom-0 z-20 flex justify-center pb-4 sm:pb-5">
                            <div className="flex items-center gap-2 rounded-full border border-white/20 bg-black/35 px-3 py-1.5 backdrop-blur-sm">
                                {heroSlides.map((slide, index) => (
                                    <button
                                        key={`dot-${slide.title}-${index}`}
                                        type="button"
                                        onClick={() => setActiveCarouselSlide(index)}
                                        aria-label={`Go to banner ${index + 1}`}
                                        className={`h-2 rounded-full transition-all duration-300 ${activeCarouselSlide === index ? 'w-7 bg-emerald-500' : 'w-2 bg-white/45 hover:bg-white/80'}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="absolute right-0 bottom-0 left-0 z-20 h-1 bg-white/15">
                            <div
                                key={`progress-${activeCarouselSlide}`}
                                className="h-full origin-left animate-[heroProgress_6.5s_linear] bg-emerald-500"
                            />
                        </div>
                    </div>

                    <aside className="relative flex items-center overflow-hidden border-t border-[oklch(0.12_0_0)] bg-[oklch(0.145_0_0)] px-5 py-6 text-white sm:px-7 sm:py-8 lg:border-t-0 lg:px-8 lg:py-10">
                        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.004)_28%,rgba(0,0,0,0.2)_100%)]" />
                        <div className="relative w-full">
                            <div className="relative mb-5 flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Quick Links</h2>
                                    <p className="mt-1 text-sm text-white/65 sm:text-[15px]">Fast access to student services</p>
                                </div>
                                <p className="pt-1 text-[11px] font-semibold tracking-[0.2em] text-white/70 uppercase sm:text-xs">Access</p>
                            </div>
                            <div className="relative space-y-3">
                                {heroQuickLinks.map((item) =>
                                    item.external ? (
                                        <a
                                            key={item.name}
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group flex items-center justify-between rounded-2xl border border-[oklch(0.22_0_0)] bg-[oklch(0.18_0_0)] px-4 py-3.5 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition hover:border-emerald-500/30 hover:bg-[oklch(0.2_0_0)]"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm leading-tight font-semibold text-white/95 sm:text-base">{item.name}</p>
                                                <p className="mt-0.5 text-[11px] text-white/62 sm:text-xs">{item.description}</p>
                                            </div>
                                            <ExternalLink className="h-4 w-4 shrink-0 text-[#21d36b] transition group-hover:scale-105" />
                                        </a>
                                    ) : (
                                        <Link
                                            key={item.name}
                                            href={item.url}
                                            className="group flex items-center justify-between rounded-2xl border border-[oklch(0.22_0_0)] bg-[oklch(0.18_0_0)] px-4 py-3.5 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition hover:border-emerald-500/30 hover:bg-[oklch(0.2_0_0)]"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm leading-tight font-semibold text-white/95 sm:text-base">{item.name}</p>
                                                <p className="mt-0.5 text-[11px] text-white/62 sm:text-xs">{item.description}</p>
                                            </div>
                                            <ArrowRight className="h-4 w-4 shrink-0 text-[#21d36b] transition group-hover:translate-x-0.5" />
                                        </Link>
                                    ),
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            {/* Institutes Swiper */}
            <section className="bg-muted border-border border-y py-6">
                <div className="mx-auto max-w-7xl px-4">
                    {institutes.length > 0 && (
                        <div className="relative mt-2 overflow-hidden">
                            <div className="institutes-track flex flex-nowrap items-center gap-16 px-10 py-5 sm:gap-20">
                                {institutes.map((institute) => {
                                    const logoSrc = storageUrl(institute.logo, 'institutes/logos');
                                    const logoImageAttrs = imageAttrs(institute.logo_responsive, logoSrc);

                                    const logo = (
                                        <img
                                            {...logoImageAttrs}
                                            alt={institute.name}
                                            className="h-16 w-auto object-contain transition-transform duration-200 ease-out will-change-transform hover:scale-105 sm:h-20 md:h-24"
                                            loading="lazy"
                                            decoding="async"
                                            width={96}
                                            height={96}
                                        />
                                    );

                                    return institute.url ? (
                                        <Link
                                            key={`a-${institute.id}`}
                                            href={institute.url}
                                            title={institute.name}
                                            className="flex flex-shrink-0 items-center justify-center py-2"
                                        >
                                            {logo}
                                        </Link>
                                    ) : (
                                        <div key={`a-${institute.id}`} className="flex flex-shrink-0 items-center justify-center py-2">
                                            {logo}
                                        </div>
                                    );
                                })}
                                {institutes.map((institute) => {
                                    const logoSrc = storageUrl(institute.logo, 'institutes/logos');
                                    const logoImageAttrs = imageAttrs(institute.logo_responsive, logoSrc);

                                    const logo = (
                                        <img
                                            {...logoImageAttrs}
                                            alt={institute.name}
                                            className="h-16 w-auto object-contain transition-transform duration-200 ease-out will-change-transform hover:scale-105 sm:h-20 md:h-24"
                                            loading="lazy"
                                            decoding="async"
                                            width={96}
                                            height={96}
                                        />
                                    );

                                    return institute.url ? (
                                        <Link
                                            key={`b-${institute.id}`}
                                            href={institute.url}
                                            title={institute.name}
                                            className="flex flex-shrink-0 items-center justify-center py-2"
                                        >
                                            {logo}
                                        </Link>
                                    ) : (
                                        <div key={`b-${institute.id}`} className="flex flex-shrink-0 items-center justify-center py-2">
                                            {logo}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                <style>{`
                    .institutes-track {
                        animation: institutes-scroll 28s linear infinite;
                        width: max-content;
                        will-change: transform;
                    }
                    .institutes-track:hover {
                        animation-play-state: paused;
                    }
                    @keyframes institutes-scroll {
                        0%   { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                `}</style>
            </section>

            <section className="bg-muted py-10">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="grid gap-10 md:grid-cols-12">
                        <div className="md:col-span-8">
                            <div className="mb-8">
                                <h2 className="text-foreground mb-2 text-2xl font-semibold tracking-tight md:text-3xl">
                                    Latest News &amp; Announcements
                                </h2>
                                <p className="text-foreground/80 text-sm md:text-base">Stay informed with our recent updates and announcements.</p>
                            </div>

                            <div className="space-y-6">
                                {news.map((item) => {
                                    const dateLabel = formatNewsDate(item);
                                    const description = truncate(item.description ?? '', 160);
                                    const imageSrc = storageUrl(item.image, 'news/images');
                                    const newsImageAttrs = imageAttrs(item.image_responsive, imageSrc);

                                    return (
                                        <div
                                            key={item.id}
                                            className="bg-card border-border overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                                        >
                                            <div className="md:grid md:grid-cols-[325px_minmax(0,1fr)]">
                                                <div className="h-48 overflow-hidden md:h-full md:min-h-full">
                                                    <img
                                                        {...newsImageAttrs}
                                                        alt={item.title}
                                                        className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                                                        loading="lazy"
                                                        decoding="async"
                                                        width={325}
                                                        height={200}
                                                    />
                                                </div>
                                                <div className="flex p-5 md:min-w-0 md:flex-col md:p-6">
                                                    <div className="flex h-full min-w-0 flex-1 flex-col">
                                                        <div className="mb-4 flex flex-wrap gap-2">
                                                            <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-[11px] font-bold tracking-wider text-emerald-600 uppercase dark:text-[#21d36b]">
                                                                {item.category}
                                                            </span>
                                                        </div>

                                                        <h3 className="text-foreground mb-3 line-clamp-2 text-[1.1rem] leading-tight font-extrabold md:text-xl">
                                                            {item.title}
                                                        </h3>
                                                        <p className="text-muted-foreground mb-4 line-clamp-3 text-sm leading-relaxed md:text-base">
                                                            {description}
                                                        </p>

                                                        <SdgIconStrip
                                                            sdgs={item.sdgs}
                                                            className="mb-5 flex flex-wrap gap-2.5"
                                                            iconClassName="h-7 w-7 rounded-sm"
                                                        />

                                                        <div className="border-border/60 text-muted-foreground mt-auto flex items-center justify-between gap-4 border-t pt-4 text-sm">
                                                            {dateLabel ? (
                                                                <span className="flex items-center gap-2">
                                                                    <Calendar className="text-muted-foreground/80 size-4" />
                                                                    {dateLabel}
                                                                </span>
                                                            ) : (
                                                                <span />
                                                            )}

                                                            {item.link && (
                                                                <Link
                                                                    href={item.link}
                                                                    prefetch
                                                                    onClick={() => handleNewsReadMoreClick(item.id)}
                                                                    className="inline-flex items-center gap-1.5 font-bold text-[#0A7A35] transition-colors hover:text-[#08632b] hover:underline"
                                                                >
                                                                    View
                                                                    <ChevronRight className="size-4" />
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Events */}
                        <div className="md:col-span-4">
                            <div className="mb-8">
                                <h2 className="text-foreground mb-2 text-2xl font-semibold tracking-tight md:text-3xl">Latest Events</h2>
                                <p className="text-foreground/80 text-sm md:text-base">Mark your calendar.</p>
                            </div>

                            <MonthlyEventsCalendar events={events} />

                            <div className="space-y-4">
                                {events.map((event) => (
                                    <div
                                        key={event.id}
                                        className="bg-card border-border rounded-2xl border p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                                    >
                                        <div className="mb-2 flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-sm font-semibold text-[#0A7A35] md:text-base">
                                                <Calendar className="h-5 w-5" />
                                                {formatEventShortDateRange(event.start_datetime, event.end_datetime)}
                                            </div>

                                            {event.status && (
                                                <span className="rounded-full bg-[#0A7A35]/10 px-2 py-1 text-[11px] font-semibold tracking-wide text-[#0A7A35] uppercase md:text-xs">
                                                    {event.status}
                                                </span>
                                            )}
                                        </div>

                                        <h3 className="text-foreground mb-2 text-sm leading-snug font-semibold md:text-base">{event.title}</h3>

                                        <div className="text-muted-foreground flex items-center text-xs md:text-sm">
                                            <MapPin className="mr-1 h-3 w-3 text-[#0A7A35]" />
                                            <span>{event.venue}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-muted py-10">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="grid items-start gap-6 lg:grid-cols-[360px_1fr]">
                        <div className="bg-card border-border order-2 mx-auto w-full max-w-[340px] overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md lg:order-1 lg:mx-0">
                            <ExternalEmbed label="Load Facebook feed" variant="social" className="h-[520px]">
                                <iframe
                                    title="Northeastern College Facebook Page"
                                    loading="lazy"
                                    src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2FNCnianAko&tabs=timeline&width=340&height=520&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                                    width="340"
                                    height="520"
                                    style={{ border: 'none', overflow: 'hidden', width: '100%' }}
                                    scrolling="no"
                                    frameBorder="0"
                                    allowFullScreen={true}
                                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                    className="h-full w-full"
                                />
                            </ExternalEmbed>
                        </div>

                        <Deferred
                            data="videos"
                            fallback={
                                <div className="order-1 grid gap-6 lg:order-2">
                                    {Array.from({ length: 2 }).map((_, index) => (
                                        <div key={`video-skeleton-${index}`} className="border-border bg-card overflow-hidden rounded-xl border">
                                            <div className="bg-muted aspect-video animate-pulse" />
                                        </div>
                                    ))}
                                </div>
                            }
                        >
                            <div className="order-1 grid gap-6 lg:order-2">
                                {latestVideos.length > 0 ? (
                                    latestVideos.map((video) => (
                                        <div
                                            key={video.id}
                                            className="bg-card border-border overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                                        >
                                            <ExternalEmbed
                                                label={`Play ${video.title || 'Northeastern College video'}`}
                                                title={video.title || 'Northeastern College video'}
                                                posterSrc={
                                                    video.youtube
                                                        ? `https://i.ytimg.com/vi/${new URL(video.youtube.embed_url).pathname.split('/').pop()}/hqdefault.jpg`
                                                        : undefined
                                                }
                                            >
                                                {renderVideoEmbed(video)}
                                            </ExternalEmbed>
                                        </div>
                                    ))
                                ) : (
                                    <div className="border-border/60 text-muted-foreground rounded-2xl border p-6 text-sm">
                                        Videos are coming soon.
                                    </div>
                                )}
                            </div>
                        </Deferred>
                    </div>
                </div>
                <style>{`
                    @keyframes heroProgress {
                        from { transform: scaleX(0); }
                        to { transform: scaleX(1); }
                    }
                    .video-embed iframe {
                        width: 100%;
                        border: 0;
                        display: block;
                    }
                `}</style>
            </section>

            <section className="bg-muted py-10 md:py-12">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="mb-7 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                        <div>
                            <p className="mb-2 text-xs font-bold tracking-[0.22em] text-[#0A7A35] uppercase">Now Hiring</p>
                            <h2 className="text-foreground text-2xl font-semibold tracking-tight md:text-3xl">Available Careers</h2>
                            <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-relaxed md:text-base">
                                Explore current teaching and non-teaching opportunities at Northeastern College.
                            </p>
                        </div>

                        <Link
                            href="/careers"
                            prefetch
                            className="inline-flex w-fit items-center gap-2 rounded-full bg-[#0A7A35] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#08632b]"
                        >
                            View all careers
                            <ArrowRight className="size-4" />
                        </Link>
                    </div>

                    {careers.length > 0 ? (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {careers.map((career) => (
                                <article
                                    key={career.id}
                                    className="group border-border bg-card relative flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                                >
                                    <div className="mb-4 flex items-center justify-between gap-3">
                                        <span
                                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                                                career.category === 'Teaching'
                                                    ? 'bg-[#0A7A35]/10 text-[#0A7A35]'
                                                    : 'bg-muted text-muted-foreground border-border/40 border'
                                            }`}
                                        >
                                            {career.category === 'Teaching' ? (
                                                <GraduationCap className="size-3.5" />
                                            ) : (
                                                <Building2 className="size-3.5" />
                                            )}
                                            {career.category}
                                        </span>

                                        <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                                            <Clock className="text-muted-foreground/80 size-3.5" />
                                            {formatCareerDate(career.updated_at ?? career.created_at)}
                                        </span>
                                    </div>

                                    <h3 className="text-foreground line-clamp-2 text-xl leading-snug font-bold transition-colors group-hover:text-[#0A7A35]">
                                        {career.title}
                                    </h3>

                                    <div className="border-border/50 mt-4 border-b pb-4">
                                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                                            <Users className="size-4 shrink-0 text-[#0A7A35]" />
                                            <span>{formatCareerCount(career.positions_available)}</span>
                                        </div>
                                    </div>

                                    {career.description && (
                                        <p className="text-muted-foreground mt-4 line-clamp-3 text-sm leading-relaxed">{career.description}</p>
                                    )}

                                    {!!career.qualifications?.length && (
                                        <div className="mt-5 space-y-2.5">
                                            <h4 className="text-muted-foreground flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase">
                                                <Sparkles className="size-3.5 text-[#0A7A35]" />
                                                Requirements
                                            </h4>
                                            <ul className="space-y-2">
                                                {career.qualifications.slice(0, 3).map((qualification, index) => (
                                                    <li
                                                        key={`${career.id}-qualification-${index}`}
                                                        className="text-muted-foreground flex items-start gap-2 text-xs leading-normal"
                                                    >
                                                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-[#0A7A35]" />
                                                        <span className="line-clamp-2">{qualification}</span>
                                                    </li>
                                                ))}
                                                {career.qualifications.length > 3 && (
                                                    <li className="pl-6 text-[11px] font-semibold text-[#0A7A35]">
                                                        + {career.qualifications.length - 3} more requirements
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="mt-auto flex justify-end pt-6">
                                        {career.link ? (
                                            <a
                                                href={career.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => handleCareerReadMoreClick(career.id)}
                                                className="inline-flex items-center gap-2 rounded-full bg-[#0A7A35]/10 px-5 py-2.5 text-sm font-semibold text-[#0A7A35] transition-all duration-300 group-hover:bg-[#0A7A35] group-hover:text-white group-hover:shadow-md"
                                            >
                                                Apply Now
                                                <ArrowRight
                                                    className="size-4 transition-transform duration-300 group-hover:translate-x-1"
                                                    aria-hidden="true"
                                                />
                                            </a>
                                        ) : (
                                            <Link
                                                href="/careers"
                                                prefetch
                                                className="inline-flex items-center gap-2 rounded-full bg-[#0A7A35]/10 px-5 py-2.5 text-sm font-semibold text-[#0A7A35] transition-all duration-300 group-hover:bg-[#0A7A35] group-hover:text-white group-hover:shadow-md"
                                            >
                                                View Details
                                                <ArrowRight
                                                    className="size-4 transition-transform duration-300 group-hover:translate-x-1"
                                                    aria-hidden="true"
                                                />
                                            </Link>
                                        )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="border-border/60 bg-card text-muted-foreground rounded-2xl border p-6 text-sm">
                            No career opportunities are available right now. Please check back soon.
                        </div>
                    )}
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="relative overflow-hidden bg-[#063f22] py-10 text-white md:py-14">
                <img
                    src="/images/hero/about.png"
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full scale-105 object-cover opacity-20"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#052d1a]/95 via-[#063f22]/92 to-[#00963f]/80" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(244,197,66,0.24),transparent_42%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:3rem_3rem] opacity-40" />

                <div className="relative z-10 mx-auto max-w-7xl px-4">
                    <div className="mx-auto max-w-3xl text-center">
                        <p className="mb-3 text-xs font-bold tracking-[0.24em] text-[#F4C542] uppercase">Start Your NC Journey</p>
                        <h2 className="text-2xl leading-tight font-extrabold tracking-tight md:text-4xl">Ready to Join Northeastern College?</h2>
                        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-white/82 md:text-base">
                            Be part of the valley&apos;s leading educational institution.
                        </p>
                    </div>

                    <div className="mx-auto mt-8 grid max-w-4xl gap-4 md:grid-cols-3">
                        <a
                            href="https://www.facebook.com/NCnianAko"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group bg-card border-border flex min-h-24 items-center gap-4 overflow-hidden rounded-xl border p-5 text-[#102015] shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                        >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#00963f]/15 bg-[#00963f]/10 text-[#00963f] transition group-hover:border-[#00963f] group-hover:bg-[#00963f] group-hover:text-white">
                                <Send className="size-5" aria-hidden="true" />
                            </span>
                            <span className="text-left text-base leading-tight font-extrabold uppercase md:text-lg">
                                Message
                                <br />
                                Us Now
                            </span>
                        </a>

                        <Link
                            href="/course-offerings"
                            className="group bg-card border-border flex min-h-24 items-center gap-4 overflow-hidden rounded-xl border p-5 text-[#102015] shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                        >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#00963f]/15 bg-[#00963f]/10 text-[#00963f] transition group-hover:border-[#00963f] group-hover:bg-[#00963f] group-hover:text-white">
                                <BookOpen className="size-5" aria-hidden="true" />
                            </span>
                            <span className="text-left text-base leading-tight font-extrabold uppercase md:text-lg">
                                Explore Our
                                <br />
                                Programs
                            </span>
                        </Link>

                        <Link
                            href="/scholarships-and-grants"
                            className="group bg-card border-border flex min-h-24 items-center gap-4 overflow-hidden rounded-xl border p-5 text-[#102015] shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                        >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#00963f]/15 bg-[#00963f]/10 text-[#00963f] transition group-hover:border-[#00963f] group-hover:bg-[#00963f] group-hover:text-white">
                                <Award className="size-5" aria-hidden="true" />
                            </span>
                            <span className="text-left text-base leading-tight font-extrabold uppercase md:text-lg">
                                Apply For
                                <br />
                                Scholarship
                            </span>
                        </Link>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
